import CloseIcon from '@mui/icons-material/Close';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    Modal,
    TextField,
    Typography,
} from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { payPrizePayout } from '../../../data/api/prizePayouts/PrizePayoutRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB — debe coincidir con backend.

const fmtCOPLabel = (cents) =>
    Number(cents ?? 0).toLocaleString('es-CO', {
        style: 'currency', currency: 'COP', maximumFractionDigits: 0,
    }).replace('COP', '').trim();

const methodLabel = (m) => ({
    EFECTIVO: 'Efectivo',
    TRANSFERENCIA: 'Transferencia bancaria',
    NEQUI: 'Nequi',
    DAVIPLATA: 'Daviplata',
}[m] ?? m);

const fmtBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Modal de "Marcar como pagado".
 *
 * - Requiere que el pago esté en SIGNED.
 * - El soporte bancario es OPCIONAL para todos los métodos. El admin
 *   decide si lo adjunta al cerrar el pago o lo sube después con
 *   updateProof.
 * - Permite ajustar referencia / titular / notas antes de cerrar el pago.
 */
const PayPayoutModal = ({ open, onClose, payout, onPaid }) => {
    const { token } = useIndex();
    const [submitting, setSubmitting] = useState(false);
    const [paymentReference, setPaymentReference] = useState('');
    const [paymentAccountHolder, setPaymentAccountHolder] = useState('');
    const [notes, setNotes] = useState('');
    const [proofFile, setProofFile] = useState(null);
    const [proofError, setProofError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setPaymentReference(payout?.payment_reference ?? '');
        setPaymentAccountHolder(payout?.payment_account_holder ?? '');
        setNotes('');
        setProofFile(null);
        setProofError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [open, payout]);

    const handlePickFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            setProofFile(null);
            setProofError('');
            return;
        }
        if (!ALLOWED_MIME.includes(file.type)) {
            setProofError('El archivo debe ser PDF, JPG o PNG.');
            setProofFile(null);
            return;
        }
        if (file.size > MAX_BYTES) {
            setProofError('El archivo no puede superar 5 MB.');
            setProofFile(null);
            return;
        }
        setProofError('');
        setProofFile(file);
    };

    const handleClearFile = () => {
        setProofFile(null);
        setProofError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const canSubmit = !submitting && !!payout && !proofError;

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;

        const fd = new FormData();
        if (paymentReference.trim()) fd.append('payment_reference', paymentReference.trim());
        if (paymentAccountHolder.trim()) fd.append('payment_account_holder', paymentAccountHolder.trim());
        if (notes.trim()) fd.append('notes', notes.trim());
        if (proofFile) fd.append('payment_proof', proofFile);

        setSubmitting(true);
        try {
            const { data } = await payPrizePayout(token, payout.uuid, fd);
            if (data?.success) {
                Report.success(
                    'Pago entregado',
                    data?.message ?? 'El premio fue marcado como entregado.',
                    'Aceptar'
                );
                onPaid?.(data?.data);
                onClose?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => !submitting && onClose?.()}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto' }}>
                    <CardContent>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <PaidOutlinedIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6">Marcar como pagado</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Confirma la entrega y adjunta el soporte bancario.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => !submitting && onClose?.()} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        {payout?.winner && (
                            <WinnerBox>
                                <Typography variant="overline" color="text.secondary">Ganador</Typography>
                                <Typography sx={{ fontWeight: 900, color: '#10285f' }}>
                                    {payout.winner.user_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Chip size="small" label={`Doc: ${payout.winner.document ?? '-'}`} />
                                    <Chip size="small" label={`Método: ${methodLabel(payout.payment_method)}`} />
                                    <Chip
                                        size="small"
                                        color="success"
                                        label={`Neto: ${fmtCOPLabel(payout.net_amount)}`}
                                    />
                                </Box>
                            </WinnerBox>
                        )}

                        <Alert severity="info" sx={{ mt: 2 }}>
                            El soporte bancario es opcional. Si lo adjuntas debe ser PDF, JPG o
                            PNG de máximo 5 MB. También puedes subirlo después desde el detalle
                            del pago.
                        </Alert>

                        <Divider sx={{ my: 2 }} />

                        <form onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Referencia / # de transacción"
                                        value={paymentReference}
                                        onChange={(e) => setPaymentReference(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Titular cuenta destino"
                                        value={paymentAccountHolder}
                                        onChange={(e) => setPaymentAccountHolder(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        label="Notas (opcional)"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <ProofBox $hasFile={!!proofFile} $error={!!proofError}>
                                        <Box className="proof-header">
                                            <Typography variant="overline" color="text.secondary">
                                                Soporte bancario (opcional)
                                            </Typography>
                                            {proofFile && (
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    onClick={handleClearFile}
                                                    sx={{ fontSize: '0.7rem' }}
                                                >
                                                    Quitar
                                                </Button>
                                            )}
                                        </Box>

                                        {proofFile ? (
                                            <Box className="file-pill">
                                                <InsertDriveFileOutlinedIcon fontSize="small" />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 700, color: '#10285f' }}
                                                        noWrap
                                                    >
                                                        {proofFile.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {fmtBytes(proofFile.size)} · {proofFile.type}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                startIcon={<UploadFileOutlinedIcon />}
                                                fullWidth
                                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Seleccionar archivo (PDF / JPG / PNG)
                                                <input
                                                    ref={fileInputRef}
                                                    hidden
                                                    type="file"
                                                    accept="application/pdf,image/png,image/jpeg"
                                                    onChange={handlePickFile}
                                                />
                                            </Button>
                                        )}
                                        {proofError && (
                                            <Typography variant="caption" sx={{ color: '#b71c1c', mt: 0.5, display: 'block' }}>
                                                {proofError}
                                            </Typography>
                                        )}
                                    </ProofBox>
                                </Grid>
                            </Grid>

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                disabled={!canSubmit}
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #1e6a36 0%, #2e7d32 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #2e7d32 0%, #1e6a36 100%)',
                                    },
                                }}
                            >
                                <span>Confirmar entrega del premio</span>
                            </LoadingButton>
                        </form>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default PayPayoutModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 640px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.5rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(46, 125, 50, 0.14);
        border: 1px solid rgba(46, 125, 50, 0.28);
        color: #1e6a36;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
`;

const WinnerBox = styled.div`
    padding: 0.7rem 0.85rem;
    border-radius: 12px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const ProofBox = styled.div`
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: ${({ $hasFile }) =>
        $hasFile ? 'rgba(46, 125, 50, 0.05)' : 'rgba(16, 40, 95, 0.03)'};
    border: 1px dashed ${({ $hasFile, $error }) =>
        $error ? 'rgba(211, 47, 47, 0.45)'
            : $hasFile ? 'rgba(46, 125, 50, 0.4)'
                : 'rgba(15, 34, 77, 0.25)'};

    .proof-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.4rem;
    }

    .file-pill {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.6rem 0.8rem;
        border-radius: 10px;
        background: white;
        border: 1px solid rgba(46, 125, 50, 0.25);
    }
`;
